/**
 * Engagement Page
 *
 * MVP Priority - detailed engagement metrics.
 * Shows prayers, attendance, events, chat, and photo attendance.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  HeartIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CameraIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export default function EngagementPage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getEngagement();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[EngagementPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading engagement data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const prayers = data?.prayers || {};
  const attendance = data?.attendance || {};
  const events = data?.events || {};
  const chat = data?.chat || {};
  const photoAttendance = data?.photoAttendance || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Engagement Metrics</h2>
          <p className="text-gray-500 mt-1">
            Prayer, attendance, and event engagement across all organizations
          </p>
        </div>
        <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
      </div>

      {/* Prayer Section */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <HeartIcon className="w-6 h-6 text-red-500" />
          Prayer Engagement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Prayers Today" value={prayers.today} loading={loading} />
          <KPICard title="This Week" value={prayers.thisWeek} loading={loading} />
          <KPICard title="This Month" value={prayers.thisMonth} loading={loading} />
          <KPICard
            title="Answered Rate"
            value={prayers.answeredRate}
            format="percent"
            loading={loading}
          />
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h4 className="font-medium text-gray-700 mb-2">Privacy Distribution</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart data={prayers.privacyDistribution || {}} height={200} />
            )}
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Engagement Depth</h4>
            <div className="space-y-4">
              <div>
                <p className="text-4xl font-bold text-gray-900">
                  {loading ? '-' : (prayers.perUser || 0).toFixed(1)}
                </p>
                <p className="text-sm text-gray-500">prayers per user</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-gray-900">
                  {loading ? '-' : (prayers.perGroup || 0).toFixed(1)}
                </p>
                <p className="text-sm text-gray-500">prayers per group</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Attendance Section */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CalendarDaysIcon className="w-6 h-6 text-blue-500" />
          Attendance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="Overall Attendance Rate"
            value={attendance.overallRate}
            format="percent"
            loading={loading}
          />
          <KPICard
            title="At-Risk Members"
            value={attendance.atRiskMembers}
            loading={loading}
            subtitle="3+ absences or <50% attendance"
          />
          {/* Group Health Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h4 className="font-medium text-gray-700 mb-3">Group Health Distribution</h4>
            {loading ? (
              <div className="h-16 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <div className="flex gap-4">
                <div className="text-center flex-1">
                  <div className="flex items-center justify-center gap-1">
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    <span className="text-2xl font-bold text-green-600">
                      {attendance.groupHealthDistribution?.healthy || 0}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Healthy</p>
                </div>
                <div className="text-center flex-1">
                  <div className="flex items-center justify-center gap-1">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                    <span className="text-2xl font-bold text-yellow-600">
                      {attendance.groupHealthDistribution?.needsAttention || 0}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Needs Attention</p>
                </div>
                <div className="text-center flex-1">
                  <div className="flex items-center justify-center gap-1">
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                    <span className="text-2xl font-bold text-red-600">
                      {attendance.groupHealthDistribution?.atRisk || 0}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">At Risk</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <h4 className="font-medium text-gray-700 mb-2">Check-In Method Distribution</h4>
          {loading ? (
            <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
          ) : (
            <DistributionChart data={attendance.checkInMethods || {}} height={200} />
          )}
        </div>
      </section>

      {/* Events Section */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CalendarDaysIcon className="w-6 h-6 text-purple-500" />
          Events
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard title="Events This Week" value={events.thisWeek} loading={loading} />
          <KPICard title="This Month" value={events.thisMonth} loading={loading} />
          <KPICard
            title="Completion Rate"
            value={events.completionRate}
            format="percent"
            loading={loading}
          />
          <KPICard
            title="RSVP Response Rate"
            value={events.rsvpResponseRate}
            format="percent"
            loading={loading}
          />
        </div>
      </section>

      {/* Chat Section */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-green-500" />
          Chat Engagement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="Messages Per Day"
            value={chat.messagesPerDay}
            loading={loading}
            subtitle="7-day average"
          />
          <KPICard title="Active Chat Groups" value={chat.activeGroups} loading={loading} />
          <KPICard
            title="Chat Adoption Rate"
            value={chat.adoptionRate}
            format="percent"
            loading={loading}
            subtitle="% of groups with chat activity"
          />
        </div>
      </section>

      {/* Photo Attendance Section */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CameraIcon className="w-6 h-6 text-indigo-500" />
          Photo Attendance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title="Orgs Using Rekognition"
            value={photoAttendance.groupsUsing}
            loading={loading}
          />
          <KPICard
            title="Sessions This Week"
            value={photoAttendance.sessionsThisWeek}
            loading={loading}
          />
          <KPICard
            title="Avg Faces/Session"
            value={photoAttendance.avgFacesPerSession}
            format="decimal"
            loading={loading}
          />
          <KPICard
            title="Visitors This Month"
            value={photoAttendance.visitorsThisMonth}
            loading={loading}
          />
        </div>
      </section>
    </div>
  );
}
